import React, { useMemo, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye,
    faCheck,
    faTimes,
    faTruck,
    faPrint,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { USER_ROLES } from '../../utils/constants';
import DataTable from '../common/DataTable';
import ConfirmDialog from '../common/ConfirmDialog';
import { useMessageAlert } from '../common/MessageAlert';

const OrderStatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const s = (status || '').toString().toLowerCase();

    const getLabel = (key) => {
        if (!key) return '';
        const k = key.toLowerCase();
        return t(`deliveryChallanModule.actions.status.${k}`) || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const badgeStyle = (icon, color, label) => (
        <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={icon} className="text-sm" style={{ color }} />
            <span className="text-sm font-medium" style={{ color }}>{label}</span>
        </div>
    );

    if (s === 'delivered') return badgeStyle(faCheck, UI_COLORS.STATUS.SUCCESS, getLabel(s));
    if (s === 'in_transit') return badgeStyle(faTruck, UI_COLORS.STATUS.INFO, getLabel(s));

    return badgeStyle(faTimes, UI_COLORS.TEXT.MUTED, getLabel(s));
};

const DeliveryChallanPrintedTable = ({
    orders = [],
    loading = false,
    onViewOrder,
    onPrintChallan,
    onMarkDelivered,
    onRefresh,
    userRole,
}) => {
    const { t } = useTranslation();
    const { showSuccess, showError } = useMessageAlert();
    const isAdminOrSuperAdmin = [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.SALES_STAFF, USER_ROLES.OPERATIONS_MANAGER].includes(userRole);

    const [actionLoading, setActionLoading] = useState(false);
    const [showConfirmDeliver, setShowConfirmDeliver] = useState(false);
    const [confirmId, setConfirmId] = useState(null);

    // Mark an in-transit sub-order delivered, then refresh. Mirrors the pattern
    // in DeliveryChallanTable so operators can deliver from this page too.
    const handleMarkDelivered = useCallback(async (id) => {
        if (!id) return;
        setActionLoading(true);
        try {
            const res = await onMarkDelivered?.(id, {});
            if (res && res.success) {
                showSuccess(t('deliveryChallanModule.actions.markDeliveredSuccess'));
                if (onRefresh) onRefresh();
            } else {
                showError(t('deliveryChallanModule.actions.markDeliveredFailed') + (res?.error ? `: ${res.error}` : ''));
            }
        } catch (e) {
            showError(t('deliveryChallanModule.actions.markDeliveredFailed'));
        } finally {
            setActionLoading(false);
        }
    }, [onMarkDelivered, onRefresh, showSuccess, showError, t]);

    const columns = useMemo(() => [
        {
            id: 'challan_number',
            label: t('deliveryChallanModule.table.columns.challanNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.challan_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.challan_number || '-'}
                </div>
            ),
        },
        {
            id: 'sub_order_number',
            label: t('deliveryChallanModule.table.columns.subOrderNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.sub_order_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.sub_order_number || '-'}
                </div>
            ),
        },
        {
            id: 'tally_name',
            label: t('deliveryChallanModule.table.columns.tallyName'),
            sortable: true,
            sortAccessor: (o) => (o?.tally_name || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.tally_name || '-'}
                </div>
            ),
        },
        {
            id: 'location_name',
            label: t('deliveryChallanModule.table.columns.location'),
            sortable: true,
            sortAccessor: (o) => (o?.location_name || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                    {o.location_name || '-'}
                </div>
            ),
        },
        {
            id: 'vehicle_number',
            label: t('commonCols.vehicleNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.vehicle_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.vehicle_number || '-'}
                </div>
            ),
        },
        {
            id: 'total_quantity',
            label: t('deliveryChallanModule.table.columns.totalQty'),
            sortable: true,
            sortAccessor: (o) => o?.total_delivered_quantity || o?.total_quantity || 0,
            renderCell: (o) => (
                <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {o.total_delivered_quantity || o.total_quantity} {o.unit_type}
                </span>
            ),
        },
        {
            id: 'order_remark',
            label: t('commonCols.orderRemarks'),
            sortable: true,
            sortAccessor: (o) => (o?.order_remark || '').toLowerCase(),
            renderCell: (o) => {
                if (!o.order_remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>-</span>;
                return (
                    <div 
                        className="text-[11px] font-medium break-words leading-relaxed min-w-[160px] max-w-[180px] px-2 py-1.5 rounded border" 
                        title={o.order_remark} 
                        style={{ 
                            backgroundColor: 'rgba(254, 243, 199, 0.5)', 
                            color: '#92400e', 
                            borderColor: 'rgba(217, 119, 6, 0.2)',
                            whiteSpace: 'pre-wrap',
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {o.order_remark}
                    </div>
                );
            },
        },
        {
            id: 'status',
            label: t('deliveryChallanModule.table.columns.status'),
            sortable: true,
            align: 'center',
            renderCell: (o) => <OrderStatusBadge status={o.status} />,
        },
        {
            id: 'actions',
            label: t('deliveryChallanModule.table.columns.actions'),
            align: 'right',
            renderCell: (o) => {
                const statusStr = (o?.status || '').toLowerCase();
                return (
                    <div className="flex items-center justify-end space-x-2">
                        <button
                            onClick={() => onViewOrder && onViewOrder(o)}
                            className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                            title={t('deliveryChallanModule.table.actions.view')}
                            style={{ color: UI_COLORS.TEXT.PRIMARY }}
                        >
                            <FontAwesomeIcon icon={faEye} />
                        </button>

                        {isAdminOrSuperAdmin && (
                            <button
                                onClick={() => onPrintChallan && onPrintChallan(o)}
                                className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                                title="Reprint Challan"
                                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
                            >
                                <FontAwesomeIcon icon={faPrint} />
                            </button>
                        )}

                        {/* Mark Delivered (IN_TRANSIT -> DELIVERED) */}
                        {statusStr === 'in_transit' && onMarkDelivered && (
                            <button
                                onClick={() => { setConfirmId(o.id); setShowConfirmDeliver(true); }}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                title={t('deliveryChallanModule.actions.markDelivered')}
                                style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.08) }}
                            >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                                <span className="text-xs font-medium">{t('deliveryChallanModule.actions.markDelivered')}</span>
                            </button>
                        )}
                    </div>
                );
            },
        },
    ], [onViewOrder, onPrintChallan, onMarkDelivered, isAdminOrSuperAdmin, actionLoading, t]);

    return (
        <>
            <DataTable
                columns={columns}
                data={orders}
                loading={loading}
                keyField="id"
                enablePagination={false}
                defaultSort={{ field: 'updated_at', direction: 'desc' }}
                emptyState={{
                    title: t('commonCols.noPrintedChallans'),
                    message: 'No delivery challans have been printed yet.',
                }}
            />

            <ConfirmDialog
                isOpen={showConfirmDeliver}
                title={t('deliveryChallanModule.actions.markDelivered')}
                message={t('deliveryChallanModule.actions.confirmMarkDelivered')}
                confirmLabel={t('deliveryChallans.actions.confirmLabels.yesDelivered')}
                confirmVariant="success"
                loading={actionLoading}
                onCancel={() => setShowConfirmDeliver(false)}
                onConfirm={() => {
                    setShowConfirmDeliver(false);
                    if (confirmId) handleMarkDelivered(confirmId);
                }}
            />
        </>
    );
};

export default DeliveryChallanPrintedTable;
